import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "progressBar", "stepIndicator", "businessNameLabel", "businessNameInput", "employeesSection"]
  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 3 },
    completed: { type: Boolean, default: false },
    saveUrl: String
  }

  connect() {
    console.log("Onboarding Survey controller connected")
    console.log("Survey completed:", this.completedValue)

    // Only show modal if survey not completed
    if (!this.completedValue) {
      // Small delay to ensure DOM is ready
      setTimeout(() => this.showModal(), 500)
    }

    this.initializeWizard()
    this.setupUserTypeListeners()
    this.setupTurboCacheHandler()
  }

  setupTurboCacheHandler() {
    // Ensure modal is hidden before Turbo caches the page
    document.addEventListener('turbo:before-cache', () => {
      this.hideModal()
    })
  }

  setupUserTypeListeners() {
    // Listen for user type selection changes
    const userTypeInputs = document.querySelectorAll('input[name="user_type"]')
    userTypeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.updateBusinessNameLabel(e.target.value)
      })
    })
  }

  updateBusinessNameLabel(userType) {
    if (this.hasBusinessNameLabelTarget && this.hasBusinessNameInputTarget) {
      if (userType === 'government') {
        // Change to "Agency name"
        this.businessNameLabelTarget.textContent = 'Agency name'
        this.businessNameInputTarget.placeholder = 'Enter your agency name'

        // Hide employees section for government
        if (this.hasEmployeesSectionTarget) {
          this.employeesSectionTarget.classList.add('d-none')
          // Clear any selected employee option
          const selectedEmployee = document.querySelector('input[name="employees"]:checked')
          if (selectedEmployee) {
            selectedEmployee.checked = false
          }
        }
      } else {
        // Change to "Business name"
        this.businessNameLabelTarget.textContent = 'Business name'
        this.businessNameInputTarget.placeholder = 'Enter your business name'

        // Show employees section for business
        if (this.hasEmployeesSectionTarget) {
          this.employeesSectionTarget.classList.remove('d-none')
        }
      }
    }
  }

  initializeWizard() {
    // Ensure we're on step 1
    this.goToStep(1)
    this.updateProgressBar()

    // Add event listeners to navigation buttons
    this.element.querySelectorAll('.next-step').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        const step = parseInt(e.currentTarget.getAttribute('data-step'))
        this.goToNextStep(step)
      })
    })

    this.element.querySelectorAll('.prev-step').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        const step = parseInt(e.currentTarget.getAttribute('data-step'))
        this.goToPrevStep(step)
      })
    })

    // Handle final submission button
    const finishButton = this.element.querySelector('.finish-survey')
    if (finishButton) {
      finishButton.addEventListener('click', this.handleSubmit.bind(this))
    }
  }

  showModal() {
    const modal = bootstrap.Modal.getOrCreateInstance(this.element)
    modal.show()
  }

  hideModal() {
    const modal = bootstrap.Modal.getInstance(this.element)
    if (modal) {
      modal.hide()
    }
  }

  updateProgressBar() {
    const progressPercentage = ((this.currentStepValue - 1) / (this.totalStepsValue - 1)) * 100
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progressPercentage}%`
    }

    // Update step indicators
    this.stepIndicatorTargets.forEach(indicator => {
      const stepNum = parseInt(indicator.getAttribute('data-step'))
      indicator.classList.remove('active', 'completed')

      if (stepNum < this.currentStepValue) {
        indicator.classList.add('completed')
      } else if (stepNum === this.currentStepValue) {
        indicator.classList.add('active')
      }
    })
  }

  goToStep(step) {
    // Hide all steps
    this.stepTargets.forEach(stepEl => {
      stepEl.classList.remove('active')
      stepEl.classList.add('d-none')
    })

    // Show target step
    const targetStep = this.stepTargets.find(el => parseInt(el.getAttribute('data-step')) === step)
    if (targetStep) {
      targetStep.classList.remove('d-none')
      targetStep.classList.add('active')
    }

    this.currentStepValue = step
    this.updateProgressBar()
  }

  goToNextStep(currentStepNum) {
    if (this.validateStep(currentStepNum) && currentStepNum < this.totalStepsValue) {
      this.goToStep(currentStepNum + 1)
    }
  }

  goToPrevStep(currentStepNum) {
    if (currentStepNum > 1) {
      this.goToStep(currentStepNum - 1)
    }
  }

  validateStep(step) {
    let isValid = true
    const errorContainer = document.getElementById(`step${step}-error-message`)

    // Clear previous error
    if (errorContainer) {
      errorContainer.classList.add('d-none')
      errorContainer.innerHTML = ''
    }

    // Validate based on step
    if (step === 1) {
      // Check if user type is selected
      const userTypeSelected = document.querySelector('input[name="user_type"]:checked')
      if (!userTypeSelected) {
        this.showStepError(step, "Please select your user type")
        isValid = false
      }
    } else if (step === 2) {
      // Validate all required fields in step 2
      const userType = document.querySelector('input[name="user_type"]:checked')?.value
      const businessName = document.querySelector('input[name="business_name"]')?.value
      const employees = document.querySelector('input[name="employees"]:checked')
      const industry = document.querySelector('select[name="industry"]')?.value
      const country = document.querySelector('select[name="country"]')?.value
      const website = document.querySelector('input[name="website"]')?.value

      if (!businessName || !businessName.trim()) {
        this.showStepError(step, "Please enter your business name")
        isValid = false
      } else if (userType !== 'government' && !employees) {
        // Only validate employees for non-government users
        this.showStepError(step, "Please select number of employees")
        isValid = false
      } else if (!industry) {
        this.showStepError(step, "Please select your industry")
        isValid = false
      } else if (!country) {
        this.showStepError(step, "Please select your country")
        isValid = false
      } else if (!website || !website.trim()) {
        this.showStepError(step, "Please enter your website")
        isValid = false
      } else if (!this.isValidUrl(website)) {
        this.showStepError(step, "Please enter a valid website URL (e.g., example.com or https://example.com)")
        isValid = false
      }
    }

    return isValid
  }

  isValidUrl(url) {
    // Accept URLs with or without protocol
    // Pattern: domain.com or http://domain.com or https://domain.com
    const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i
    return urlPattern.test(url)
  }

  showStepError(step, message) {
    const errorContainer = document.getElementById(`step${step}-error-message`)
    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="fa-regular fa-exclamation-circle text-danger me-2"></i>
          <div>${message}</div>
        </div>
      `
      errorContainer.classList.remove('d-none')
    }
  }

  async handleSubmit(e) {
    e.preventDefault()

    // Collect survey data
    const userType = document.querySelector('input[name="user_type"]:checked')?.value
    const businessName = document.querySelector('input[name="business_name"]')?.value
    const employees = document.querySelector('input[name="employees"]:checked')?.value
    const industry = document.querySelector('select[name="industry"]')?.value
    const country = document.querySelector('select[name="country"]')?.value
    const website = document.querySelector('input[name="website"]')?.value

    const surveyData = {
      onboarding_survey: {
        completed: true,
        completed_at: new Date().toISOString(),
        user_type: userType,
        business_name: businessName,
        employees: employees,
        industry: industry,
        country: country,
        website: website
      }
    }

    console.log("Submitting onboarding survey:", surveyData)

    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          'Accept': 'application/json'
        },
        body: JSON.stringify(surveyData)
      })

      const data = await response.json()

      if (data.success) {
        console.log("Survey saved successfully!")

        // Update the data attribute and value to prevent modal from showing again
        this.element.dataset.onboardingSurveyCompletedValue = "true"
        this.completedValue = true

        this.hideModal()

        // Clear Turbo cache to ensure fresh data on next visit
        if (typeof Turbo !== 'undefined' && Turbo.cache) {
          Turbo.cache.clear()
        }

        // Show a success message
        if (data.message) {
          // You can use flash messages or toast notifications here
          console.log(data.message)
        }
      } else {
        console.error("Error saving survey:", data.message)
        window.location.reload()
      }
    } catch (error) {
      console.error("Error submitting survey:", error)
      window.location.reload()
    }
  }
}
